import type { AccessStatus, EFrom } from 'reactApp/components/SharingNewBiz/SharingNew.types';
import type { DomainAccess, EPromoTooltip } from 'reactApp/components/SharingWindow/Sharing.types';
import type { AlbumItem } from 'reactApp/modules/albums/albums.types';
import type { EditorItem } from 'reactApp/modules/editor/editor.types';
import type { FavoriteFile } from 'reactApp/modules/favorites/favorites.types';
import type { FeedItem } from 'reactApp/modules/feed/feed.types';
import type { GalleryItem } from 'reactApp/modules/gallery/gallery.types';
import type { HomeItem, XrayItemType } from 'reactApp/modules/home/home.types';
import type { WeblinkDownloadsCounts } from 'reactApp/modules/public/public.types';
import type { SearchItem } from 'reactApp/modules/search/search.types';
import type { SharedItem } from 'reactApp/modules/shared/shared.type';
import type { CloudFile, CloudItem, EStorageType } from 'reactApp/modules/storage/storage.types';
import type { AccessRights, Kind, Sort, VirusScan } from 'reactApp/types/Tree';

export interface IRenameItem {
    item: CloudItem;
    newId: string;
}

export type PublishItem = FavoriteFile | HomeItem | EditorItem | SharedItem | FeedItem | GalleryItem | SearchItem | AlbumItem;

export interface AddFile extends WeblinkDownloadsCounts {
    virus_scan: VirusScan;
    type: 'file';
    home: string;
    weblink?: string;
    size: number;
    name: string;
    kind: Kind;
    hash: string;
    id: string;
    mtime: number;
    descriptorId?: string;
    mode_bitmask?: number;
}

interface AddFolder extends WeblinkDownloadsCounts {
    virus_scan: VirusScan;
    type: 'folder';
    home?: string;
    tree: string;
    rev: number;
    grev: number;
    weblink?: string;
    size: number;
    name: string;
    kind: Kind;
    id: string;
    mtime: number;
    count: {
        folders: number;
        files: number;
    };
    mode_bitmask?: number;
}

export enum FolderType {
    folder = 'folder',
    family = 'family',
}

export type TItem = 'folder' | 'file';

export type AddItem = AddFile | AddFolder;

export interface AddFileSuccessAction {
    parent: string;
    storage: EStorageType;
    items: AddItem[];
    reason?: EModifyReason;
    allowedExt?: string[];
}

export interface UploadFileEndedAction {
    ids: string[];
}

export interface RemoveFileEndedAction {
    ids: string[];
}

export interface CopyFileEndedAction {
    ids: string[];
}

export interface RemoveFileSuccessAction {
    ids: string[];
    reason?: EModifyReason;
    descriptors?: Record<
        string,
        {
            descriptorId: string;
            hash: string;
            isDirectory: boolean;
        }
    >;
}

export interface MoveItemsSuccessAction {
    storage: EStorageType;
    oldIds: string[];
    newItems: CloudItem[];
    newParent: string;
}

export interface UpdateItemAction {
    count?: {
        folders: number;
        files: number;
    };
    tree: string;
    name: string;
    grev: number;
    kind: Kind;
    rev: number;
    type: TItem;
    hash?: string;
    home: string;
    mtime: number;
    size: number;
}

export interface SetSortAction {
    sort: Sort;
    id: string;
}

export interface MountActionSuccess {
    id: string;
    home?: string;
}

export interface ToggleWeblinkAccessRights {
    id: string;
    type?: AccessRights;
}

export interface ToggleWeblinkDownloadable {
    id: string;
    downloadable?: boolean;
}

export interface UpdateWeblinkCountDownloads {
    id: string;
    count_downloads: number;
}

export interface ResetWeblinkCountDownloads {
    id: string;
}

export interface UnmountActionSuccess {
    id: string;
}

export interface UpdateWeblinkAction {
    id: string;
    weblink: string;
    item?: CloudItem;
    uniqueId?: string;
}

export interface UnpublishWeblinkAction {
    ids: string[];
}

export interface UpdateWeblinkAutoDelete {
    id: string;
    autoDelete: boolean;
    expires?: number;
}

export interface UpdateWeblinkAutoDeleteRequest {
    item: PublishItem;
    expires: number;
    xrayItemType: XrayItemType;
}

export interface UpdateWeblinkExpires {
    id: string;
    expires: number;
    expiresTimeSize?: number;
}

export interface UpdateWeblinkExpiresRequest {
    item: PublishItem;
    expires: number;
    expiresTimeSize?: number;
    xrayItemType: XrayItemType;
}

export interface DeleteWeblinkExpiresRequest {
    item: PublishItem;
    xrayItemType: XrayItemType;
}

export interface ToggleWeblinkDomesticRequest {
    item: PublishItem;
    xrayItemType: XrayItemType;
}

export interface ToggleSeoData {
    item: PublishItem;
}

export interface ToggleWeblinkDomestic {
    id: string;
    domestic: boolean;
}

export interface ToggleWeblinkEditableRequest {
    id?: AccessStatus;
    item: PublishItem;
    xrayItemType: XrayItemType;
}

export interface ToggleWeblinkDownloadableRequest {
    item: PublishItem;
    isUnpublishRequest?: boolean;
    xrayItemType: XrayItemType;
}

export interface UpdateWeblinkCountDownloadsRequest {
    item: PublishItem;
    count_downloads: number;
    xrayItemType: XrayItemType;
}

export interface ToggleWeblinkUploadRequest {
    item: PublishItem;
    id?: AccessStatus;
}

export interface PublishAutoDeleteFolderAction {
    item: PublishItem;
}

export interface ICreateFolderAction {
    parentFolderId: string;
    showCreateButton?: boolean;
    showShareButton?: boolean;
    autodelete?: boolean;
    gaPrefix?: string;
    storage?: EStorageType;
    onCreateFolder?: (folderName: string) => void;
    onClose?: () => void;
    createdInToolbar?: boolean;
}

export interface CreateFamilyFolderActionInterface {
    parentFolderId: string;
    showCreateButton?: boolean;
    showShareButton?: boolean;
    autodelete?: boolean;
    gaPrefix?: string;
    storage?: EStorageType;
    onCreateFolder?: (folderName: string) => void;
    onClose?: () => void;
    createdInToolbar?: boolean;
}

export interface CreateFamilyAlbumActionInterface {
    parentFolderId: string;
    showCreateButton?: boolean;
    showShareButton?: boolean;
    autodelete?: boolean;
    gaPrefix?: string;
    storage?: EStorageType;
    onCreateFolder?: (folderName: string) => void;
    onClose?: () => void;
    createdInToolbar?: boolean;
}

export interface ICreateFolderSuccess {
    folderName: string;
    share?: boolean;
    gaPrefix?: string;
    storage?: EStorageType;
    publishPromoTooltip?: EPromoTooltip;
    createdInToolbar?: boolean;
}

export enum EModifyReason {
    copy = 'copy',
    move = 'move',
    remove = 'remove',
    add = 'add',
    rename = 'rename',
    favorites = 'favorites',
}

export interface ICopyItems {
    destination: string;
    items: CloudItem[];
}

export interface IMoveItems {
    destination: string;
    items: CloudItem[];
}

export interface IState {
    isModifyingInProgress: boolean;
    isInlineEditInProgress: boolean;
    // tempexp_14373-next-line
    inlineEditItemId?: string;
}

export enum ESnackbarType {
    COPY = 'copy',
    MOVE = 'move',
    REMOVE = 'remove',
    SAVE_TO_CLOUD = 'saveToCloud',
    MOVE_TO_TRASHBIN = 'moveToTrash',
}

export interface IRemoveItems {
    items: CloudItem[];
    // Поднимать диалог удаления
    withRemoveDialog: boolean;
    onSuccess?(): void;
}

export interface IDownloadItem {
    id?: string;
    storage?: EStorageType;
}

export interface ICreateNewItem {
    type: string;
    from: string;
    showShareOnlyButton?: boolean;
    showShareButton?: boolean;
    inHomeRoot?: boolean;
    gaPrefix?: string;
    createdInToolbar?: boolean;
}

export interface IRenameItemSuccess {
    newItem: CloudItem;
    oldId: string;
}

export interface IModifyingStopData {
    id?: string;
    storage?: EStorageType;
    reason?: EModifyReason;
}

export interface ILoadMoreRequest {
    id: string;
    storage: EStorageType;
    offset?: number;
    loadOnlyIfLessThanLimit?: boolean;
}

export interface IPublishRequest {
    item: PublishItem;
    itemStorage?: EStorageType;
    publishFrom?: string;
    respectDomesticByDefault?: boolean;
}

export interface IUnPublishRequest {
    items: PublishItem[];
}

export interface IPublishAndToggleUploadRequest {
    item: any;
    publishFrom?: string;
    defaultAccessRights?: boolean;
    autoPublish?: boolean;
    itemStorage?: EStorageType;
}

export interface IOpenPublishDialog {
    item: any;
    publishFrom?: string;
    defaultAccessRights?: AccessStatus;
    gaSuffix?: string;
    autoPublish?: boolean;
    fromEditor?: boolean;
    itemStorage?: EStorageType;
    onClose?: () => void;
    from?: EFrom;
    withPromoTooltip?: EPromoTooltip;
    autoSeo?: boolean;
    folderName?: string;
}

export interface IMountRequest {
    item: PublishItem;
}

export interface IUnmountRequest {
    item: PublishItem;
    onUnmount?: () => void;
}

export interface IShareFolderSuccess {
    id: string;
}

export interface IUnshareFolderSuccess {
    id: string;
}

export interface IAddToFavoritesSuccess {
    ids: string[];
}

export interface IAddToFavoritesRequest {
    items: CloudItem[];
    from: string;
}

export interface IRemoveFromFavoritesSuccess {
    ids: string[];
}

export interface IRemoveFromFavoritesRequest {
    items: CloudItem[];
}

export interface ICloneToCloudRequest {
    items: CloudItem[];
    autoClone?: boolean;
    noOkSnackbar?: boolean;
    destination?: string;
    source?: string;
    authSuccessPage?: string;
}

export interface EditPublicCopyItem {
    item: CloudFile;
    source: string;
    storage: EStorageType;
}

export interface EditPdfCopyItem {
    item: CloudFile;
    onPdfEdit: (item: CloudFile | null) => void;
}

export enum EInlineEditPlace {
    breadcrumbs = 'breadcrumbs',
    datalistRow = 'datalist_row',
    datalistThumbs = 'datalist_thumbs',
}

export interface IInlineEditSuccess {
    id: string;
    place: EInlineEditPlace;
    isFolder?: boolean;
}

export interface IUpdatePublicFlags {
    weblink: string;
    flags: Record<string, boolean>;
}

export interface IWeblinkSetDomainAccess {
    itemId: string;
    domain: DomainAccess;
    storage: EStorageType;
    xrayItemType: XrayItemType;
}

export interface ICreateNewFolderAndPublish {
    tooltipId?: EPromoTooltip;
}

export interface IMakeFamilyFolderSuccess {
    id: string;
}

export interface AccessRightChange {
    item: PublishItem;
    isEditable: boolean;
    isReadOnlyProperty: boolean;
    newAccessRight: AccessStatus;
    storage: EStorageType;
    xrayItemType: XrayItemType;
}
export interface MakeFamilyFolderCommonSuccessInterface {
    id: string;
}
